/**
 * TECHNICAL HEALTH, THE CLIENT (docs/ANALYTICS.md *Technical health*; the server's half is server/metrics.js).
 *
 * ERRORS. `installErrorReports()` (main.tsx, at boot) listens for uncaught errors and unhandled rejections; the React
 * error boundaries and the WebGL context-loss handler call `reportClientError` themselves. Each distinct error —
 * message + top stack frame — is reported ONCE per page load, and at most MAX_REPORTS in all, so an error thrown every
 * frame is one event, not sixty a second. It rides the ordinary analytics batch (`track`), with the build id; the first
 * STACK_REPORTS also carry the first frames of the stack (`stack`, ≤ 400 characters, scrubbed like the message) — the
 * sample the admin Tech health page opens — so a page load adds at most ~1.2 KB of stacks to its batches.
 *
 * PERFORMANCE. `startClientPerf` (App, on mount) and `noteSceneLoaded` (App, when the loader's progress reaches 1)
 * gather ONE `client_perf` per page load: time to the first scene, each scene's first load, and the average frame CPU
 * over the first minute after the first scene — read off the meter the renderer already keeps (render/pixi/sharedApp
 * `frameCpuTotals`), never a new per-frame probe. It is sent on the first flush after the minute, or when the tab is
 * hidden, the page goes away or the game unmounts — with whatever was measured by then.
 *
 * Both go nowhere when analytics is off (Do Not Track, Global Privacy Control, the player's opt-out): `track` drops
 * them like any other event.
 */
import { onAnalyticsFlush, track } from "./analytics";

/** The deploy this bundle came from (vite.config.ts `VITE_BUILD_ID`: the commit on Railway, else the build time). */
export const BUILD_ID: string = import.meta.env.VITE_BUILD_ID || (import.meta.env.DEV ? "dev" : "unknown");

export type ClientErrorKind = "error" | "rejection" | "react" | "webgl";

/** Distinct errors reported per page load, at most. */
const MAX_REPORTS = 10;
/** Of those, how many carry their stack (the first errors of a page load are the cause; the rest usually its echo). */
const STACK_REPORTS = 3;
const STACK_MAX = 400;
const seen = new Set<string>();
let reports = 0;

/** Cut the query string and hash off every URL (they can carry codes and tokens) and this site's own origin. */
function scrub(s: string): string {
  const out = s.replace(/(https?:\/\/[^\s?#)'"]+)[?#][^\s)'"]*/g, "$1");
  return typeof location !== "undefined" ? out.split(location.origin).join("") : out;
}

/** "file:line:col" of the first frame of a stack (V8's "at f (url:1:2)" / "at url:1:2" / an eval's "…, <anonymous>:1:2)",
 *  or Firefox/Safari's "f@url:1:2"). */
export function topFrame(stack: string | undefined): string {
  if (!stack) return "";
  for (const line of stack.split("\n")) {
    const m = line.match(/(?:\(|@|\s)([^()\s@]+:\d+:\d+)\)?\s*$/);
    if (m) return scrub(m[1]!);
  }
  return "";
}

/** The stack's first lines, scrubbed, at most STACK_MAX characters; "" when there is none. */
export function stackOf(stack: string | undefined): string {
  if (!stack) return "";
  return scrub(stack.split("\n").slice(0, 10).map((l) => l.trim()).filter(Boolean).join("\n")).slice(0, STACK_MAX);
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === "string") return err;
  try { return JSON.stringify(err) ?? String(err); } catch { return String(err); }
}

/**
 * Report one error. `frame` is given when there is no stack to read it from (a cross-origin "Script error." still has
 * a file and line). Cheap after the first report of an error: one string join and a Set lookup.
 */
export function reportClientError(kind: ClientErrorKind, err: unknown, frame?: string): void {
  if (reports >= MAX_REPORTS) return;
  try {
    const msg = scrub(messageOf(err)).slice(0, 160);
    const top = (frame ? scrub(frame) : topFrame(err instanceof Error ? err.stack : undefined)).slice(0, 160);
    const key = `${msg}|${top}`;
    if (seen.has(key)) return;
    seen.add(key);
    reports++;
    const stack = reports <= STACK_REPORTS ? stackOf(err instanceof Error ? err.stack : undefined) : "";
    track("client_error", { kind, msg, frame: top, build: BUILD_ID, ...(stack ? { stack } : {}) });
  } catch { /* reporting an error must never raise another */ }
}

let installed = false;
/** Listen for uncaught errors and unhandled rejections, once. */
export function installErrorReports(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (e) => {
    if (!(e instanceof ErrorEvent)) return;   // a resource that failed to load is not a script error
    if (e.error) reportClientError("error", e.error);
    else reportClientError("error", e.message || "Script error.", e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : "");
  });
  window.addEventListener("unhandledrejection", (e) => reportClientError("rejection", e.reason));
}

// ── the performance sample ────────────────────────────────────────────────────────────────────────────────────────

/** How long after the first scene the frame-CPU window closes, and the frames it needs to count as a measurement
 *  (a hidden tab does not tick, so a minute can hold far fewer). Short of them, it waits RETRY_MS more, a few times. */
const CPU_WINDOW_MS = 60_000;
const CPU_MIN_FRAMES = 600;
const RETRY_MS = 30_000;
const MAX_RETRIES = 4;

type LoadKey = "loadGarden" | "loadGreenhouse" | "loadOther";
interface PerfReaders {
  /** render/pixi/sharedApp `frameCpuTotals` — passed in so this module never pulls the renderer into the site's chunk. */
  frameCpuTotals(): { ms: number; frames: number };
  lowGraphics(): boolean;
}
interface PerfState {
  readers: PerfReaders;
  mountAt: number;
  fromNav: boolean;
  firstSceneMs?: number;
  loads: Partial<Record<LoadKey, number>>;
  starts: Set<number>;
  cpu0?: { ms: number; frames: number };
  cpu?: { avgMs: number; frames: number };
  timer?: ReturnType<typeof setTimeout>;
}
let perf: PerfState | null = null;
let perfSent = false;

function deviceClass(): "desktop" | "tablet" | "mobile" {
  try {
    const touchOnly = matchMedia("(pointer: coarse)").matches && !matchMedia("(any-pointer: fine)").matches;
    if (!touchOnly) return "desktop";
    return Math.min(screen.width, screen.height) < 600 ? "mobile" : "tablet";
  } catch { return "desktop"; }
}

/** Did this page load start on /play (so time-to-first-scene counts from the navigation, download and all)? */
function loadedOnPlay(): boolean {
  try {
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    return !!nav && new URL(nav.name).pathname.startsWith("/play");
  } catch { return false; }
}

function closeCpuWindow(tries: number): void {
  const p = perf;
  if (!p?.cpu0) return;
  const now = p.readers.frameCpuTotals();
  const frames = now.frames - p.cpu0.frames;
  if (frames < CPU_MIN_FRAMES && tries < MAX_RETRIES) { p.timer = setTimeout(() => closeCpuWindow(tries + 1), RETRY_MS); return; }
  p.timer = undefined;
  if (frames > 0) p.cpu = { avgMs: (now.ms - p.cpu0.ms) / frames, frames };
}

function sendPerf(): void {
  const p = perf;
  if (perfSent || !p || p.firstSceneMs === undefined) return;
  perfSent = true;
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  track("client_perf", {
    firstSceneMs: p.firstSceneMs, fromNav: p.fromNav, ...p.loads,
    ...(p.cpu ? { cpuMs: p.cpu.avgMs, frames: p.cpu.frames } : {}),
    dpr, lowGfx: p.readers.lowGraphics(), device: deviceClass(), build: BUILD_ID,
  });
}

/** The game mounted: start gathering this page load's sample. Returns the unmount (which sends what was measured). */
export function startClientPerf(readers: PerfReaders): () => void {
  if (perfSent) return () => {};
  const state: PerfState = { readers, mountAt: performance.now(), fromNav: loadedOnPlay(), loads: {}, starts: new Set() };
  perf = state;
  const off = onAnalyticsFlush((final) => { if (final || state.cpu) sendPerf(); });
  return () => {
    sendPerf();
    off();
    if (state.timer) clearTimeout(state.timer);
    if (perf === state) perf = null;
  };
}

/** A scene finished loading (the loader reached 100 %); `startedAt` is the loader's own `performance.now()` stamp. */
export function noteSceneLoaded(screen: string, startedAt: number): void {
  const p = perf;
  if (!p || p.starts.has(startedAt)) return;
  p.starts.add(startedAt);
  const t = performance.now();
  if (p.firstSceneMs === undefined) {
    p.firstSceneMs = Math.round(p.fromNav ? t : t - p.mountAt);
    p.cpu0 = p.readers.frameCpuTotals();
    p.timer = setTimeout(() => closeCpuWindow(0), CPU_WINDOW_MS);
  }
  const key: LoadKey = screen === "garden" ? "loadGarden" : screen === "greenhouse" ? "loadGreenhouse" : "loadOther";
  if (p.loads[key] === undefined) p.loads[key] = Math.round(t - startedAt);
}
